<template>
  <v-app>
    <v-container fluid>
      <!-- ================== SEARCH PANEL ================== -->
      <v-expansion-panels v-model="panel" focusable>
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Search Form
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-form @submit.prevent="submit">
              <v-row class="pa-2" dense>
                <v-col lg="3" md="4" cols="12">
                  <v-autocomplete
                    label="Contacts"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    class="px-2"
                    v-model="contact_id.value.value"
                    :error-messages="contact_id.errorMessage.value"
                    :items="getContactList"
                    :item-title="item => item ? `${item.code} - ${item.name}` : ''" 
                    autocomplete="off"   
                    item-value="id"
                    hide-details="auto"
                    clearable
                  ></v-autocomplete>
                </v-col>

                <v-col lg="3" md="4" cols="12">
                  <v-date-input
                    label="From Date"
                    density="compact"
                    single-line
                    prepend-icon="" 
                    prepend-inner-icon="mdi-calendar"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="from_date.value.value"
                    :error-messages="from_date.errorMessage.value"
                    autocomplete="off"
                    hide-details="auto"
                    clearable
                  ></v-date-input>
                </v-col>

                <v-col lg="3" md="4" cols="12">
                  <v-date-input
                    label="To Date"
                    density="compact"
                    single-line
                    prepend-icon="" 
                    prepend-inner-icon="mdi-calendar"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="to_date.value.value"
                    :error-messages="to_date.errorMessage.value"
                    autocomplete="off"
                    hide-details="auto"
                    clearable
                  ></v-date-input>
                </v-col>

                <v-col lg="2" md="4" cols="12" class="d-flex">
                  <v-btn class="bg-brand" type="submit" ref="searchButton">Search</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- ================== REPORT PANEL ================== -->
    <v-container fluid class="">
      <v-expansion-panels v-model="panelReport" focusable>
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Sales Reports
          </v-expansion-panel-title>
          <v-expansion-panel-text>

            <!-- ================== Filters + Export ================== -->
            <v-row class="py-2" align="center">
              <v-col md="4" cols="12">
                <v-text-field
                  v-model="search"
                  append-inner-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  clearable
                ></v-text-field>
              </v-col>

              <v-col md="5" cols="12">
                <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                  <!-- <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">CSV</v-btn> -->
                  <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">EXCEL</v-btn>
                  <!-- <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">PDF</v-btn> -->
                </v-btn-group>
              </v-col>
            </v-row>

            <!-- ================== Tabs for Reports ================== -->
            <v-tabs v-model="activeTab" grow>
              <v-tab value="flat">Flat Table</v-tab>
              <!-- <v-tab value="grouped">Grouped</v-tab> -->
              <v-tab value="summary">Seller Summary</v-tab>
            </v-tabs>
            <v-window v-model="activeTab" class="mt-2">

              <!-- ========== FLAT TABLE ========= -->
              <v-window-item value="flat">
                <v-data-table
                  :headers="flatHeaders"
                  :items="flatRows"
                  :search="search"
                  dense
                  class="elevation-1"
                  :items-per-page="-1"
                  hide-default-footer
                >
                  <template v-slot:item.index="{ index }">{{ ++index }}</template>
                </v-data-table>
              </v-window-item>

              <!-- ========== GROUPED REPORT ========= -->
              <v-window-item value="grouped">
                <v-accordion>
                  <v-accordion-item
                    v-for="(projects, seller) in groupedData"
                    :key="seller"
                    :title="seller"
                  >
                    <v-list>
                      <v-list-item
                        v-for="project in projects"
                        :key="project.id"
                      >
                        <v-list-item-content>
                          <strong>{{ project.name }} ({{ project.code }})</strong>
                          <v-list dense>
                            <v-list-item
                              v-for="author in project.sales_orders"
                              :key="author.id"
                            >
                              <v-list-item-content>
                                {{ author.author_name }} - Agreed: {{ author.agreed_amount }}, Paid: {{ author.paid_amount }}, Due: {{ author.due_amount }}
                                <v-list dense>
                                  <v-list-item
                                    v-for="p in author.payments"
                                    :key="p.id"
                                  >
                                    Payment: {{ p.amount }} | Date: {{ p.payment_date || 'N/A' }}
                                  </v-list-item>
                                </v-list>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-accordion-item>
                </v-accordion>
              </v-window-item>

              <!-- ========== SELLER SUMMARY ========= -->
              <v-window-item value="summary">
                <v-data-table
                  :headers="summaryHeaders"
                  :items="summaryRows"
                  dense
                  class="elevation-1"
                  :search="search"
                  :items-per-page="-1"
                  hide-default-footer
                >
                    <template v-slot:item.index="{ index }">{{ ++index }}</template>
                </v-data-table>
              </v-window-item>

            </v-window>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
  </v-app>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import Axios from "axios";
import formattedDate from "@/plugins/dateFormatter";
import { prefix, fileServerUrl } from "@/store/api";

export default {
    components: {
    },
    data: () => ({
        itemsPerPage: 10,
        panel: [0],
        contactPersonInfo: [0],
        search: "",
        isEntryMode: true,
        dialog: false,
      }),

    methods: {
      openAddAuthor() {
        this.dialog = true;
      },
      exportToExcel() {
        if (this.activeTab === "flat") {
          const headers = [
            '#SL', 'Seller', 'Project Code', 'Author Seller', 'Author',
            'Agreed Amount', 'Paid Amount', 'Due Amount', 'Payment Count'
          ];

          const data = [headers];

          this.flatRows.forEach((r, i) => {
            data.push([
              i + 1,
              r.seller ?? '',
              r.project_code ?? '',
              r.author_seller ?? '',
              r.author ?? '',
              r.agreed ?? '',
              r.paid ?? '',
              r.due ?? '',
              r.payment_count ?? ''
            ]);
          });

          this.$exportTableToExcel(data, 'projects_flat_' + this.$fileDate());

          // exportFn(data, `projects_flat_${fileDate}`);
          return;
        }

        if (this.activeTab === "summary") {
          const headers = [
            '#SL', 'Seller', 'Total Projects', 'Total Authors',
            'Total Agreed', 'Total Paid', 'Total Due'
          ];

          const data = [headers];

          this.summaryRows.forEach((r, i) => {
            data.push([
              i + 1,
              r.seller ?? '',
              r.total_projects ?? 0,
              r.total_authors ?? 0,
              (r.total_agreed ?? 0).toString(),
              (r.total_paid ?? 0).toString(),
              (r.total_due ?? 0).toString()
            ]);
          });

            // exportFn(data, `projects_summary_${fileDate}`);
          this.$exportTableToExcel(data, 'projects_summary_' + this.$fileDate());
          return;
        }

        const headers = [
          '#SL', 'Seller', 'Project Code', 'Author Seller', 'Author',
          'Agreed Amount', 'Paid Amount', 'Due Amount', 'Payment Count'
        ];

        const data = [headers];

        this.flatRows.forEach((r, i) => {
          data.push([
            i + 1,
            r.seller ?? '',
            r.project_code ?? '',
            r.author_seller ?? '',
            r.author ?? '',
            r.agreed ?? '',
            r.paid ?? '',
            r.due ?? '',
            r.payment_count ?? ''
          ]);
        });

        // exportFn(data, `projects_flat_${fileDate}`);
        this.$exportTableToExcel(data, 'projects_flat_' + this.$fileDate());
      }
    },

    setup() {
      const panel = ref([0]);
      const panelReport = ref([0]);
      const activeTab = ref("flat");
      const search = ref("");

      // --------------------- Form fields ---------------------
      const { handleSubmit } = useForm({
        validationSchema: yup.object({
          from_date: yup.date().nullable().label("From Date"),
          to_date: yup.date().nullable().label("To Date"),
        }),
      });

      const from_date = useField("from_date");
      const to_date = useField("to_date");
      const contact_id = useField("contact_id");

      const store = useStore();
      const reportData = ref([]);
      const filters = ref({ with_details: false });

      // --------------------- Customer list ---------------------

      if (store.getters.getContactList.length === 0) {
        store.dispatch("getContactList");
      }
      const getContactList = computed(() => store.getters.getContactList || []);

      // --------------------- Submit Function ---------------------
      const submit = handleSubmit(async (values) => {
        const from = formattedDate(from_date.value.value);
        const to = formattedDate(to_date.value.value);

        filters.value = {
          contact_id: contact_id.value.value,
          from_date: from,
          to_date: to,
          with_details: filters.value.with_details,
        };

        try {
          const { data } = await Axios.post(prefix + 'project/project-contacted-by-sales-report', filters.value);
          reportData.value = data.data;
        } catch (err) {
          console.error(err);
        }
      });

      // --------------------- OPTION A: Flat Table ---------------------
      const flatRows = computed(() => {
        const rows = [];
        reportData.value.forEach(project => {
          project.sales_orders?.forEach(order => {
            const paymentTotal = order.payments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
            rows.push({
              seller: project.contacted_by?.name,
              project_code: project.code,
              author_seller: order.contacted_by?.name,
              author: order.author_name,
              agreed: order.agreed_amount,
              paid: order.paid_amount,
              due: order.due_amount,
              payment_count: order.payments?.length || 0
            });
          });
        });
        return rows;
      });
      const flatHeaders = [
        { title: "#SL", key: "index", align: "center" },
        { title: "Seller", key: "seller" },
        { title: "Project Code", key: "project_code" },
        { title: "Author Seller", key: "author_seller" },
        { title: "Author", key: "author" },
        { title: "Agreed Amount", key: "agreed" },
        { title: "Paid Amount", key: "paid" },
        { title: "Due Amount", key: "due" },
        { title: "Payment Count", key: "payment_count" }
      ];

      // --------------------- OPTION B: Grouped ---------------------
      const groupedData = computed(() => {
        const grouped = {};
        reportData.value.forEach(project => {
          const seller = project.contacted_by?.name || "Unknown Seller";
          if (!grouped[seller]) grouped[seller] = [];
          grouped[seller].push(project);
        });
        return grouped;
      });

      onMounted(() => {
        from_date.value.value = new Date();
        to_date.value.value = new Date();
        const from = formattedDate(from_date.value.value);
        const to = formattedDate(to_date.value.value);

        submit.value = {
          contact_id: contact_id.value.value,
          from_date: from,
          to_date: to,
          with_details: true,
        };
      });

      // --------------------- OPTION C: Seller Summary ---------------------
      const summaryRows = computed(() => {
        const sellers = {};
        reportData.value.forEach(project => {
          const seller = project.contacted_by?.name || "Unknown Seller";
          if (!sellers[seller]) {
            sellers[seller] = {
              seller,
              total_projects: 0,
              total_authors: 0,
              total_agreed: 0,
              total_paid: 0,
              total_due: 0
            };
          }
          sellers[seller].total_projects += 1;
          project.sales_orders?.forEach(order => {
            sellers[seller].total_authors += 1;
            sellers[seller].total_agreed += parseFloat(order.agreed_amount || 0);
            sellers[seller].total_paid += parseFloat(order.paid_amount || 0);
            sellers[seller].total_due += parseFloat(order.due_amount || 0);
          });
        });
        return Object.values(sellers);
      });
      const summaryHeaders = [
      { title: "#SL", key: "index", align: "center" },
        { title: "Seller", key: "seller" },
        { title: "Total Projects", key: "total_projects" },
        { title: "Total Authors", key: "total_authors" },
        { title: "Total Agreed", key: "total_agreed" },
        { title: "Total Paid", key: "total_paid" },
        { title: "Total Due", key: "total_due" }
      ];

      return {
        panel,
        panelReport,
        activeTab,
        search,
        from_date,
        to_date,
        contact_id,
        getContactList,
        submit,
        flatRows,
        flatHeaders,
        groupedData,
        summaryRows,
        summaryHeaders,
        // exportToExcel,
      };
    }

  };
</script>



